import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import workflowService from '../../../services/workflowService';

const WorkflowBuilder = ({ workflow, onSave, onCancel }) => {
  const { user } = useAuth();
  const [workflowData, setWorkflowData] = useState({
    name: '',
    description: '',
    trigger_type: 'manual',
    trigger_conditions: {},
    status: 'draft'
  });
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (workflow) {
      setWorkflowData({
        name: workflow?.name || '',
        description: workflow?.description || '',
        trigger_type: workflow?.trigger_type || 'manual',
        trigger_conditions: workflow?.trigger_conditions || {},
        status: workflow?.status || 'draft'
      });
      setWorkflowSteps(workflow?.workflow_steps || []);
    }
  }, [workflow]);

  const handleInputChange = (field, value) => {
    setWorkflowData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWorkflowStep = () => {
    const newStep = {
      id: `temp_${Date.now()}`,
      step_order: workflowSteps?.length + 1,
      name: '',
      step_type: 'email',
      configuration: {},
      delay_minutes: 0,
      is_active: true
    };
    setWorkflowSteps(prev => [...prev, newStep]);
  };

  const updateWorkflowStep = (stepIndex, field, value) => {
    setWorkflowSteps(prev => prev?.map((step, index) => 
      index === stepIndex ? { ...step, [field]: value } : step
    ));
  };

  const removeWorkflowStep = (stepIndex) => {
    setWorkflowSteps(prev => prev?.filter((_, index) => index !== stepIndex));
  };

  const moveStep = (stepIndex, direction) => {
    const newSteps = [...workflowSteps];
    const targetIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
    
    if (targetIndex >= 0 && targetIndex < newSteps?.length) {
      [newSteps[stepIndex], newSteps[targetIndex]] = [newSteps?.[targetIndex], newSteps?.[stepIndex]];
      
      // Update step orders
      newSteps?.forEach((step, index) => {
        step.step_order = index + 1;
      });
      
      setWorkflowSteps(newSteps);
    }
  };

  const handleSave = async () => {
    if (!workflowData?.name?.trim()) {
      setError('Workflow name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let savedWorkflow;
      
      if (workflow?.id) {
        // Update existing workflow
        const { data, error: updateError } = await workflowService?.updateWorkflow(
          workflow?.id,
          {
            ...workflowData,
            user_id: user?.id
          }
        );
        
        if (updateError) {
          setError(updateError);
          return;
        }
        
        savedWorkflow = data;
      } else {
        // Create new workflow
        const { data, error: createError } = await workflowService?.createWorkflow({
          ...workflowData,
          user_id: user?.id
        });
        
        if (createError) {
          setError(createError);
          return;
        }
        
        savedWorkflow = data;
      }

      // Save workflow steps
      if (savedWorkflow?.id && workflowSteps?.length > 0) {
        for (const [index, step] of workflowSteps?.entries()) {
          const stepData = {
            ...step,
            workflow_id: savedWorkflow?.id,
            step_order: index + 1
          };
          
          if (step?.id?.startsWith('temp_')) {
            // Create new step
            delete stepData?.id;
            await workflowService?.createWorkflowStep(stepData);
          } else if (step?.id) {
            // Update existing step
            await workflowService?.updateWorkflowStep(step?.id, stepData);
          }
        }
      }

      onSave?.();
    } catch (err) {
      setError('Failed to save workflow');
    } finally {
      setLoading(false);
    }
  };

  const stepTypeOptions = [
    { value: 'email', label: 'Send Email', icon: 'Mail' },
    { value: 'sms', label: 'Send SMS', icon: 'MessageSquare' },
    { value: 'call', label: 'Schedule Call', icon: 'Phone' },
    { value: 'wait', label: 'Wait', icon: 'Clock' },
    { value: 'condition', label: 'Condition', icon: 'GitBranch' }
  ];

  const triggerTypeOptions = [
    { value: 'manual', label: 'Manual Trigger' },
    { value: 'time_based', label: 'Time-based' },
    { value: 'event_based', label: 'Event-based' },
    { value: 'conditional', label: 'Conditional' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {workflow ? 'Edit Workflow' : 'Create New Workflow'}
        </h2>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !workflowData?.name?.trim()}
            iconName={loading ? "Loader2" : "Save"}
            iconPosition="left"
            className={loading ? "animate-spin" : ""}
          >
            {loading ? 'Saving...' : 'Save Workflow'}
          </Button>
        </div>
      </div>
      {error && (
        <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-destructive text-sm">{error}</span>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Workflow Name *
            </label>
            <Input
              type="text"
              value={workflowData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              placeholder="Enter workflow name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Trigger Type
            </label>
            <select
              value={workflowData?.trigger_type}
              onChange={(e) => handleInputChange('trigger_type', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              {triggerTypeOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Description
          </label>
          <textarea
            value={workflowData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            placeholder="Describe what this workflow does"
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
          />
        </div>

        {/* Workflow Steps */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-foreground">Workflow Steps</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={addWorkflowStep}
              iconName="Plus"
              iconPosition="left"
            >
              Add Step
            </Button>
          </div>

          {workflowSteps?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="GitBranch" size={48} className="mx-auto mb-4 opacity-50" />
              <p>No steps added yet. Click "Add Step" to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {workflowSteps?.map((step, index) => (
                <div key={step?.id || index} className="border border-border rounded-lg p-4 bg-muted/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="font-medium text-foreground">
                        Step {index + 1}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveStep(index, 'up')}
                        disabled={index === 0}
                        iconName="ChevronUp"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveStep(index, 'down')}
                        disabled={index === workflowSteps?.length - 1}
                        iconName="ChevronDown"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeWorkflowStep(index)}
                        iconName="Trash2"
                        className="text-destructive hover:text-destructive"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Step Name
                      </label>
                      <Input
                        type="text"
                        value={step?.name}
                        onChange={(e) => updateWorkflowStep(index, 'name', e?.target?.value)}
                        placeholder="Enter step name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Step Type
                      </label>
                      <select
                        value={step?.step_type}
                        onChange={(e) => updateWorkflowStep(index, 'step_type', e?.target?.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      >
                        {stepTypeOptions?.map(option => (
                          <option key={option?.value} value={option?.value}>
                            {option?.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Delay (minutes)
                      </label>
                      <Input
                        type="number"
                        value={step?.delay_minutes}
                        onChange={(e) => updateWorkflowStep(index, 'delay_minutes', parseInt(e?.target?.value) || 0)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilder;